import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useClientsMain } from '../../hooks/useClientsMain';
import { ClientsAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CreateClientButton, FiltersSection } from '../interaction';

export const Main = () => {
  const { t } = useTranslation('clientsModule');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook que maneja toda la lógica de negocio
  const {
    selectedColumns,
    clients,
    isLoading,
    isFetching,
    error,
    total,
    clientsViewProps,
    searchTerm,
    customerOrigins,
    handleSearch,
    handleCustomerOriginsChange,
    handleColumnsChange,
    refetch,
    isFetchingNextPage,
    hasActiveFilters,
  } = useClientsMain();

  // 🎯 Obtener columnas para el ColumnSelector usando el hook de AG Grid
  const { tanStackColumns } = useAgGridColumns({ selectedColumns });

  // Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // 🎯 Efecto para marcar que la carga inicial terminó
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad && isLoading) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Botón crear - Solo visible si la API devuelve > 0 clientes */}
      {total > 0 && (
        <div className="absolute right-4 top-2">
          <CreateClientButton />
        </div>
      )}
      {/* Header: buscador por nombre y botón crear */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              searchTerm={searchTerm}
              onSearch={handleSearch}
              customerOrigins={customerOrigins}
              onCustomerOriginsChange={handleCustomerOriginsChange}
              columns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
            />
          </div>
          {/* Controles derecha - Right controls */}
          <div className="flex items-center gap-3">
            <TotalResults total={total} />
          </div>
        </div>
      </div>

      {/* 🎯 AG Grid - NUNCA se desmonta del DOM (regla crítica) */}
      <div
        className={`transition-all duration-300 mb-4 ${
          // Mostrar tabla si: hay datos o búsqueda activa
          clients.length > 0 || hasActiveFilters ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden'
        } ${
          // Durante búsquedas (no primera carga): opacity-50
          isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        <ClientsAgGridTable {...clientsViewProps} />
      </div>

      {/* 🎯 Estado vacío - Solo cuando no hay datos */}
      {clients.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          customAction={hasActiveFilters ? null : <CreateClientButton />}
        />
      )}
    </div>
  );
};
